import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ArrowDownTrayIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon, ShareIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../context/ProjectsContext';
import { projectService } from '../services/projectService';
import { projectPlanService } from '../services/projectPlanService';
import { devCapacityService } from '../services/devCapacityService';
import { getWeekRanges } from '../utils/dateUtils';
import ShareProjectModal from './projects/ShareProjectModal';
import * as XLSX from 'xlsx';

const CACHE_TTL_MS = 5 * 60 * 1000;

function getWeekStatus(weekStartDate) {
  const now = new Date();
  const start = new Date(weekStartDate);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const todayUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  if (todayUTC > end) return 'past';
  if (todayUTC >= start && todayUTC <= end) return 'current';
  return 'future';
}

function formatHours(seconds) {
  if (!seconds) return '—';
  return `${(seconds / 3600).toFixed(1)}h`;
}

function SortIcon({ columnKey, sortConfig }) {
  if (sortConfig.key !== columnKey) return <span className="ml-1 text-slate-300 dark:text-slate-600">↕</span>;
  return <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
}

function MultiSelectFilter({ label, options, optionLabels = {}, selected, onChange, isOpen, onToggle }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onToggle(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onToggle]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => onToggle(!isOpen)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${selected.length > 0
          ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300'
          : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
          }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="bg-indigo-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
            {selected.length}
          </span>
        )}
        <span className="text-xs">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 z-50 min-w-[180px] max-h-60 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1"
          onMouseDown={e => e.stopPropagation()}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">No options</div>
          ) : (
            options.map(opt => (
              <label
                key={opt}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                {optionLabels[opt] || opt}
              </label>
            ))
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selected.map(v => (
            <span
              key={v}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
            >
              {v}
              <button
                type="button"
                onClick={() => toggle(v)}
                className="hover:text-indigo-900 dark:hover:text-indigo-100 font-bold"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectPlan() {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const { getProjectById } = useProjects();
  const contextProject = getProjectById(projectId);

  const [fetchedProject, setFetchedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [weeklyCapacity, setWeeklyCapacity] = useState({});
  const [savingCell, setSavingCell] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({ status: [], assignee: [], issue_type: [], epic_key: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [capacityMode, setCapacityMode] = useState('capacity');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  const [weeklyDevCapacity, setWeeklyDevCapacity] = useState({});
  const hasDataRef = useRef(false);
  const [isBackgroundFetching, setIsBackgroundFetching] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullScreen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen]);

  const project = contextProject || fetchedProject;
  const weeks = useMemo(() => getWeekRanges(project), [project?.start_date, project?.end_date, project?.week_start_day]);
  const sessionCacheKey = useMemo(
    () => `plan_issues_${projectId}_${weeks.map(w => w.start_date).join(',')}`,
    [projectId, weeks]
  );

  // Returns { data, timestamp } if cache is valid, null if missing or expired
  const getFromCache = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(sessionCacheKey);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      const isExpired = Date.now() - timestamp > CACHE_TTL_MS;
      if (isExpired) {
        sessionStorage.removeItem(sessionCacheKey);
        return null;
      }
      return { data, timestamp };
    } catch {
      sessionStorage.removeItem(sessionCacheKey);
      return null;
    }
  }, [sessionCacheKey]);

  const saveToCache = useCallback((data) => {
    try {
      sessionStorage.setItem(sessionCacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch {
      // sessionStorage might be full or disabled — fail silently
    }
  }, [sessionCacheKey]);
  // Fetch project if not in context
  useEffect(() => {
    if (!projectId) return;
    if (!project) {
      projectService.getProject(projectId)
        .then(setFetchedProject)
        .catch(err => toast.error(err.message));
    }
  }, [projectId, project]);

  // Fetch capacity from dev capacity table


  useEffect(() => {
    if (!projectId) return;
    devCapacityService.getProjectDevelopers(projectId).then(devs => {
      const capacityByWeek = {};
      const devCapacityByWeek = {};
      weeks.forEach((week) => {
        const total = devs.reduce((sum, dev) => {
          const cap = dev.weekly_capacities?.find(wc => wc.week_start_date === week.start_date);
          return sum + (Number(cap?.hours) || 0);
        }, 0);
        const devTotal = devs
          .filter(dev => {
            const roleTypeName = typeof dev.role_type === 'string'
              ? dev.role_type
              : dev.role_type?.name ?? '';
            return roleTypeName.toLowerCase() === 'dev';
          })
          .reduce((sum, dev) => {
            const cap = dev.weekly_capacities?.find(wc => wc.week_start_date === week.start_date);
            return sum + (Number(cap?.hours) || 0);
          }, 0);
        capacityByWeek[week.start_date] = total;
        devCapacityByWeek[week.start_date] = devTotal;
      });
      setWeeklyCapacity(capacityByWeek);
      setWeeklyDevCapacity(devCapacityByWeek);
    }).catch(() => { });
  }, [projectId, weeks]);

  // Fetch plan issues  uses sessionStorage cache to avoid re-fetching on revisit
  // Cache expires after 5 minutes. User can force refresh via "Sync with Jira" button.
  useEffect(() => {
    if (!project || !weeks.length) return;

    const cached = getFromCache();
    if (cached) {
      // Cache hit — load instantly without hitting Jira
      setIssues(cached.data);
      hasDataRef.current = true;
      setLastSyncedAt(new Date(cached.timestamp));
      setLoading(false);
      return;
    }

    // Cache miss or expired  fetch from Jira
    // Use ref to check if we already have data without adding issues to deps
    const hasStaleData = hasDataRef.current;
    if (!hasStaleData) {
      // First visit  no data yet, show full loading spinner
      setLoading(true);
    } else {
      // Revisit after cache expiry keep table visible, show subtle background indicator
      setIsBackgroundFetching(true);
    }

    projectPlanService.getPlanIssues(projectId, weeks)
      .then(data => {
        const fetchedIssues = data.issues || [];
        setIssues(fetchedIssues);
        hasDataRef.current = true; // mark that we now have data
        saveToCache(fetchedIssues);
        setLastSyncedAt(new Date());
      })
      .catch(err => toast.error(err.message))
      .finally(() => {
        setLoading(false);
        setIsBackgroundFetching(false);
      });
  }, [project, projectId, weeks, getFromCache, saveToCache]);

  // Total capacity overall
  const totalCapacityAll = useMemo(() => {
    return Object.values(weeklyCapacity).reduce((a, b) => a + b, 0);
  }, [weeklyCapacity]);

  const totalDevCapacityAll = useMemo(() => {
    return Object.values(weeklyDevCapacity).reduce((a, b) => a + b, 0);
  }, [weeklyDevCapacity]);


  const activeCapacityByWeek = useMemo(
    () => capacityMode === 'dev' ? weeklyDevCapacity : weeklyCapacity,
    [capacityMode, weeklyDevCapacity, weeklyCapacity]
  );

  const activeCapacityTotal = useMemo(
    () => capacityMode === 'dev' ? totalDevCapacityAll : totalCapacityAll,
    [capacityMode, totalDevCapacityAll, totalCapacityAll]
  );
  // Total consumed per week: logged hours for past/current weeks, planned hours (with pending edits) for future weeks
  const totalConsumedPerWeek = useMemo(() => {
    const totals = {};
    weeks.forEach(w => { totals[w.start_date] = 0; });
    issues.forEach(issue => {
      weeks.forEach(w => {
        const status = getWeekStatus(w.start_date);
        if (status === 'past' || status === 'current') {
          totals[w.start_date] += issue.weekly_logged?.[w.start_date] ?? 0;
        } else {
          const planned = pendingEdits[`${issue.key}-${w.start_date}`] ?? issue.weekly_planned?.[w.start_date] ?? 0;
          totals[w.start_date] += planned;
        }
      });
    });
    return totals;
  }, [issues, weeks, pendingEdits]);
  // Total consumed overall
  const totalConsumedAll = useMemo(() => {
    return Object.values(totalConsumedPerWeek).reduce((a, b) => a + b, 0);
  }, [totalConsumedPerWeek]);


  // Weekly totals
  const weeklyTotals = useMemo(() => {
    const totals = {};
    weeks.forEach(w => { totals[w.start_date] = 0; });
    issues.forEach(issue => {
      weeks.forEach(w => {
        const logged = issue.weekly_logged?.[w.start_date] ?? 0;
        const planned = pendingEdits[`${issue.key}-${w.start_date}`] ?? issue.weekly_planned?.[w.start_date] ?? 0;
        const status = getWeekStatus(w.start_date);
        if (status === 'past' || status === 'current') {
          totals[w.start_date] += logged;
        } else {
          totals[w.start_date] += planned;
        }
      });
    });
    return totals;
  }, [issues, weeks, pendingEdits]);

  const filterOptions = useMemo(() => {
    const epicMap = {};
    issues.forEach(i => {
      if (i.epic_key) epicMap[i.epic_key] = i.epic_summary || i.epic_key;
    });
    return {
      statuses: [...new Set(issues.map(i => i.status).filter(Boolean))],
      assignees: [...new Set(issues.map(i => i.assignee).filter(Boolean))],
      issue_types: [...new Set(issues.map(i => i.issue_type).filter(Boolean))],
      epics: Object.keys(epicMap),
      epicLabels: Object.fromEntries(
        Object.entries(epicMap).map(([key, summary]) => [key, `${key} — ${summary}`])
      ),
    };
  }, [issues]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const filteredAndSortedIssues = useMemo(() => {
    let result = [...issues];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.key?.toLowerCase().includes(q) ||
        i.summary?.toLowerCase().includes(q) ||
        i.assignee?.toLowerCase().includes(q) ||
        i.epic_key?.toLowerCase().includes(q)
      );
    }

    if (filters.status.length) result = result.filter(i => filters.status.includes(i.status));
    if (filters.assignee.length) result = result.filter(i => filters.assignee.includes(i.assignee));
    if (filters.issue_type.length) result = result.filter(i => filters.issue_type.includes(i.issue_type));
    if (filters.epic_key.length) result = result.filter(i => filters.epic_key.includes(i.epic_key));

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal, bVal;
        if (sortConfig.key === 'total_logged') {
          aVal = weeks.reduce((sum, w) => sum + (a.weekly_logged?.[w.start_date] ?? 0), 0);
          bVal = weeks.reduce((sum, w) => sum + (b.weekly_logged?.[w.start_date] ?? 0), 0);
        } else if (sortConfig.key === 'estimate') {
          aVal = a.original_estimate_seconds ?? 0;
          bVal = b.original_estimate_seconds ?? 0;
        } else if (sortConfig.key === 'key') {
          // Extract numeric part after the dash e.g. PT-123 → 123
          const extractNum = (k) => {
            const match = (k ?? '').toString().match(/(\d+)$/);
            return match ? parseInt(match[1], 10) : 0;
          };
          aVal = extractNum(a.key);
          bVal = extractNum(b.key);
        } else if (sortConfig.key?.startsWith('week_')) {
          const weekDate = sortConfig.key.replace('week_', '');
          const status = getWeekStatus(weekDate);
          if (status === 'past' || status === 'current') {
            aVal = a.weekly_logged?.[weekDate] ?? 0;
            bVal = b.weekly_logged?.[weekDate] ?? 0;
          } else {
            aVal = a.weekly_planned?.[weekDate] ?? 0;
            bVal = b.weekly_planned?.[weekDate] ?? 0;
          }
        } else {
          aVal = (a[sortConfig.key] ?? '').toString().toLowerCase();
          bVal = (b[sortConfig.key] ?? '').toString().toLowerCase();
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [issues, searchQuery, filters, sortConfig, weeks]);

  const onToggleStatus = useCallback((val) => setOpenFilter(val ? 'status' : null), []);
  const onToggleAssignee = useCallback((val) => setOpenFilter(val ? 'assignee' : null), []);
  const onToggleType = useCallback((val) => setOpenFilter(val ? 'type' : null), []);
  const onToggleEpic = useCallback((val) => setOpenFilter(val ? 'epic' : null), []);
  const activeFilterCount = Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);


  const handleCellChange = useCallback((issueKey, weekStartDate, value) => {
    const hours = Math.max(0, parseFloat(value) || 0);
    const capacity = weeklyCapacity[weekStartDate] ?? Infinity;
    const currentTotal = weeklyTotals[weekStartDate] ?? 0;
    const currentCellValue = pendingEdits[`${issueKey}-${weekStartDate}`] ??
      issues.find(i => i.key === issueKey)?.weekly_planned?.[weekStartDate] ?? 0;
    const newTotal = currentTotal - currentCellValue + hours;

    if (weeklyCapacity[weekStartDate] !== undefined && weeklyCapacity[weekStartDate] > 0 && newTotal > capacity) {
      toast.error(`Cannot exceed weekly capacity of ${capacity}h`);
      return;
    }

    setPendingEdits(prev => ({
      ...prev,
      [`${issueKey}-${weekStartDate}`]: hours
    }));
  }, [weeklyCapacity, weeklyTotals, pendingEdits, issues]);

  const handleCellBlur = useCallback(async (issueKey, weekStartDate) => {
    const key = `${issueKey}-${weekStartDate}`;
    if (!(key in pendingEdits)) return;
    const hours = pendingEdits[key];

    setSavingCell(key);
    try {
      await projectPlanService.saveEstimate(projectId, issueKey, weekStartDate, hours);
      setIssues(prev => prev.map(issue => {
        if (issue.key !== issueKey) return issue;
        return {
          ...issue,
          weekly_planned: { ...issue.weekly_planned, [weekStartDate]: hours }
        };
      }));
      setPendingEdits(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingCell(null);
    }
  }, [pendingEdits, projectId]);

  const handleSync = useCallback(async () => {
    if (!project || !weeks.length) return;
    setIsSyncing(true);
    try {
      // Always clear cache before syncing  forces fresh fetch from Jira
      sessionStorage.removeItem(sessionCacheKey);
      const data = await projectPlanService.getPlanIssues(projectId, weeks);
      const fetchedIssues = data.issues || [];
      setIssues(fetchedIssues);
      saveToCache(fetchedIssues);
      setLastSyncedAt(new Date());
      setPendingEdits({});
      setFilters({ status: [], assignee: [], issue_type: [], epic_key: [] });
      setSearchQuery('');
      setOpenFilter(null);
      toast.success('Plan view synced with Jira successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to sync with Jira');
    } finally {
      setIsSyncing(false);
    }
  }, [project, projectId, weeks, sessionCacheKey, saveToCache]);

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'done' || s === 'closed') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    if (s === 'in progress') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    if (s === 'blocked') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  };

  const getIssueTypeColor = (type) => {
    const t = type?.toLowerCase();
    if (t === 'bug') return 'text-red-500';
    if (t === 'story') return 'text-green-500';
    if (t === 'epic') return 'text-purple-500';
    return 'text-blue-500';
  };
  const handleExportToExcel = () => {
    if (!issues.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Issue Key', 'Parent', 'Summary', 'Status', 'Type', 'Original Estimate', ...weeks.map(w => w.label)];

    const rows = filteredAndSortedIssues.map(issue => {
      const weekValues = weeks.map(w => {
        const status = getWeekStatus(w.start_date);
        const logged = issue.weekly_logged?.[w.start_date] ?? 0;
        const planned = issue.weekly_planned?.[w.start_date] ?? 0;
        return status === 'past' ? logged : planned;
      });

      const parent = issue.parent_key
        ? (issue.parent_summary ? `${issue.parent_key} — ${issue.parent_summary}` : issue.parent_key)
        : '—';

      return [
        issue.key,
        parent,
        issue.summary,
        issue.status,
        issue.issue_type,
        issue.original_estimate_seconds ? (issue.original_estimate_seconds / 3600).toFixed(1) + 'h' : '—',
        ...weekValues
      ];
    });

    const data = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Project Plan');

    const dateStr = new Date().toISOString().split('T')[0];
    const projectName = project?.name?.replace(/\s+/g, '-') || 'project';
    XLSX.writeFile(workbook, `${projectName}-plan-${dateStr}.xlsx`);
  };

  if (!project && !loading) {
    return (
      <div className="p-6 text-center text-slate-500 dark:text-slate-400">
        Project not found.
      </div>
    );
  }

  return (
    <div className={`flex flex-col transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-50 bg-white dark:bg-slate-900 p-4 overflow-hidden' : 'p-4 h-full'}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            ← Back to Projects
          </button>
          {project && (
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              <strong className="text-slate-800 dark:text-slate-200">{project.name}</strong> — Project Plan
            </span>
          )}
        </div>
        <div className="flex items-start gap-2 flex-wrap">
          <div className="relative">
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
            />
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">🔍</span>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(p => !p)}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${activeFilterCount > 0 ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200'}`}
          >
            Filters {activeFilterCount > 0 && <span className="bg-indigo-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>}
          </button>
          <button
            type="button"
            onClick={() => setIsFullScreen(prev => !prev)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title={isFullScreen ? 'Exit full screen' : 'Expand to full screen'}
          >
            {isFullScreen ? <ArrowsPointingInIcon className="size-4" /> : <ArrowsPointingOutIcon className="size-4" />}
            {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
          </button>
          <div className="flex flex-col items-end gap-0.5">
            <button
              type="button"
              onClick={handleSync}
              disabled={loading || isSyncing || isBackgroundFetching || savingCell !== null}
              className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-4 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`size-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync with Jira'}
            </button>
            {isBackgroundFetching && (
              <span className="text-[10px] text-indigo-500 dark:text-indigo-400 flex items-center gap-1">
                <ArrowPathIcon className="size-3 animate-spin" />
                Refreshing...
              </span>
            )}
            {lastSyncedAt && !isSyncing && !isBackgroundFetching && (
              <span className="text-[10px] text-slate-400 dark:text-slate-500">
                Last synced: {lastSyncedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleExportToExcel}
            disabled={loading || isSyncing || !issues.length}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-4 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="size-4" />
            Export to Excel
          </button>
          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            disabled={!project}
            className="inline-flex items-center gap-2 rounded-lg border border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/30 px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ShareIcon className="size-4" />
            Share
          </button>
        </div>
      </div>

      {showShareModal && project && (
        <ShareProjectModal project={project} onClose={() => setShowShareModal(false)} />
      )}

      {showFilters && (
        <div className="mb-4 flex flex-wrap items-start gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <MultiSelectFilter
            label="Status"
            options={filterOptions.statuses}
            selected={filters.status}
            onChange={v => setFilters(p => ({ ...p, status: v }))}
            isOpen={openFilter === 'status'}
            onToggle={onToggleStatus}
          />
          <MultiSelectFilter
            label="Assignee"
            options={filterOptions.assignees}
            selected={filters.assignee}
            onChange={v => setFilters(p => ({ ...p, assignee: v }))}
            isOpen={openFilter === 'assignee'}
            onToggle={onToggleAssignee}
          />
          <MultiSelectFilter
            label="Type"
            options={filterOptions.issue_types}
            selected={filters.issue_type}
            onChange={v => setFilters(p => ({ ...p, issue_type: v }))}
            isOpen={openFilter === 'type'}
            onToggle={onToggleType}
          />
          <MultiSelectFilter
            label="Epic"
            options={filterOptions.epics}
            optionLabels={filterOptions.epicLabels}
            selected={filters.epic_key}
            onChange={v => setFilters(p => ({ ...p, epic_key: v }))}
            isOpen={openFilter === 'epic'}
            onToggle={onToggleEpic}
          />
          {activeFilterCount > 0 && (
            <button
              onClick={() => setFilters({ status: [], assignee: [], issue_type: [], epic_key: [] })}
              className="px-3 py-1.5 text-sm rounded-lg border border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-600 dark:bg-rose-900/30 dark:text-rose-400 hover:bg-rose-100 transition-colors self-start"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-slate-500 dark:text-slate-400">
          <ArrowPathIcon className="size-5 animate-spin" />
          <span className="text-sm">Loading project plan... This may take a moment.</span>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
          <table className="w-full border-collapse text-sm min-w-[800px]">
            <thead>
              {/* Total/Week row */}
              <tr className="bg-blue-50 dark:bg-blue-950 border-b border-slate-200 dark:border-slate-700">
                <th className="sticky left-0 z-20 bg-blue-50 dark:bg-blue-950 px-4 py-2 min-w-[120px]" />
                <th className="sticky left-[120px] z-20 bg-blue-50 dark:bg-blue-950 px-3 py-2 min-w-[250px]" />
                <th className="sticky left-[370px] z-20 bg-blue-50 dark:bg-blue-950 px-3 py-2 min-w-[80px]" />
                <th className="sticky left-[450px] z-20 bg-blue-50 dark:bg-blue-950 px-3 py-2 text-left text-xs font-semibold text-blue-700 dark:text-blue-300 min-w-[160px] w-[160px]">
                  <div className="flex flex-col gap-1">
                    <span>Total / Week</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setCapacityMode('capacity')}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${capacityMode === 'capacity'
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200'
                          }`}
                      >
                        Total Capacity
                      </button>
                      <button
                        type="button"
                        onClick={() => setCapacityMode('dev')}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${capacityMode === 'dev'
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200'
                          }`}
                      >
                        Dev Hours
                      </button>
                    </div>
                  </div>
                </th>
                <th className="sticky left-[610px] z-20 bg-blue-50 dark:bg-blue-950 px-3 py-2 text-right text-xs font-semibold text-blue-700 dark:text-blue-300 min-w-[80px]">
                  {activeCapacityTotal.toFixed(0)}
                </th>
                <th className="sticky left-[690px] z-30 bg-blue-50 dark:bg-blue-950 px-3 py-2 min-w-[80px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]" />
                {weeks.map(w => (
                  <th key={w.start_date} className="relative z-0 px-3 py-2 text-center text-xs font-semibold text-blue-700 dark:text-blue-300 min-w-[90px]">
                    {(activeCapacityByWeek[w.start_date] ?? 0) > 0
                      ? activeCapacityByWeek[w.start_date].toFixed(2)
                      : '—'}
                  </th>
                ))}
              </tr>

              {/* Total Consumed row */}
              <tr className="bg-emerald-50 dark:bg-emerald-950 border-b border-slate-200 dark:border-slate-700">
                <th className="sticky left-0 z-20 bg-emerald-50 dark:bg-emerald-950 px-4 py-2 min-w-[120px]" />
                <th className="sticky left-[120px] z-20 bg-emerald-50 dark:bg-emerald-950 px-3 py-2 min-w-[250px]" />
                <th className="sticky left-[370px] z-20 bg-emerald-50 dark:bg-emerald-950 px-3 py-2 min-w-[80px]" />
                <th className="sticky left-[450px] z-20 bg-emerald-50 dark:bg-emerald-950 px-3 py-2 text-left text-xs font-semibold text-emerald-700 dark:text-emerald-300 min-w-[160px] w-[160px]">
                  Total Consumed
                </th>
                <th className="sticky left-[610px] z-20 bg-emerald-50 dark:bg-emerald-950 px-3 py-2 text-right text-xs font-semibold text-emerald-700 dark:text-emerald-300 min-w-[80px]">
                  {totalConsumedAll.toFixed(0)}
                </th>
                <th className="sticky left-[690px] z-30 bg-emerald-50 dark:bg-emerald-950 px-3 py-2 min-w-[80px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]" />
                {weeks.map(w => {
                  const isFuture = getWeekStatus(w.start_date) === 'future';
                  const value = totalConsumedPerWeek[w.start_date] ?? 0;
                  return (
                    <th key={w.start_date} className="relative z-0 px-3 py-2 text-center text-xs font-semibold text-emerald-700 dark:text-emerald-300 min-w-[90px]">
                      {isFuture && value > 0 && (
                        <span className="block text-[9px] font-normal text-emerald-600/70 dark:text-emerald-400/70 leading-tight">
                          (planned)
                        </span>
                      )}
                      {value > 0 ? value.toFixed(2) : '—'}
                    </th>
                  );
                })}
              </tr>
              {/* Week headers */}
              <tr className="bg-slate-50 dark:bg-slate-900 border-b-2 border-slate-200 dark:border-slate-700">
                {/*
                  Sticky column offsets — each left-[Xpx] is the cumulative sum of all previous column widths:
                  left-0        = 0 (Issue: 120px)
                  left-[120px]  = 120 (Summary: 250px)
                  left-[370px]  = 120 + 250 (Status: 80px)
                  left-[450px]  = 370 + 80 (Assignee: 160px)
                  left-[610px]  = 450 + 160 (Total Logged: 80px)
                  left-[690px]  = 610 + 80 (Estimate: 80px)
                  If any column width changes, update all subsequent offsets accordingly.
                */}
                {[
                  { key: 'key', label: 'Issue', extraClass: 'sticky left-0 z-20 bg-slate-50 dark:bg-slate-900 text-left min-w-[120px]' },
                  { key: 'summary', label: 'Issue Summary', extraClass: 'sticky left-[120px] z-20 bg-slate-50 dark:bg-slate-900 text-left min-w-[250px]' },
                  { key: 'status', label: 'Status', extraClass: 'sticky left-[370px] z-20 bg-slate-50 dark:bg-slate-900 text-left min-w-[80px]' },
                  { key: 'assignee', label: 'Assignee', extraClass: 'sticky left-[450px] z-20 bg-slate-50 dark:bg-slate-900 text-left min-w-[160px] w-[160px]' },
                  { key: 'total_logged', label: 'Total Logged', extraClass: 'sticky left-[610px] z-20 bg-slate-50 dark:bg-slate-900 text-right min-w-[80px]' },
                  { key: 'estimate', label: 'Estimate', extraClass: 'sticky left-[690px] z-20 bg-slate-50 dark:bg-slate-900 text-left min-w-[80px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]' },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 select-none transition-colors ${col.extraClass}`}
                  >
                    {col.label}<SortIcon columnKey={col.key} sortConfig={sortConfig} />
                  </th>
                ))}

                {weeks.map((w, i) => {
                  const status = getWeekStatus(w.start_date);
                  const weekSortKey = `week_${w.start_date}`;
                  return (
                    <th
                      key={w.start_date}
                      onClick={() => handleSort(weekSortKey)}
                      className={`px-3 py-3 text-center text-xs font-semibold min-w-[90px] whitespace-nowrap cursor-pointer select-none transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 ${status === 'current'
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                        : 'text-slate-600 dark:text-slate-300'
                        }`}
                    >
                      <span className="block text-[10px] text-slate-400 dark:text-slate-500">Week {i + 1}</span>
                      {w.label}
                      <SortIcon columnKey={weekSortKey} sortConfig={sortConfig} />
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredAndSortedIssues.length === 0 ? (
                <tr>
                  <td colSpan={6 + weeks.length} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    {issues.length === 0 ? 'No issues found for this project.' : 'No issues match your search or filters.'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedIssues.map(issue => {
                  const totalLogged = weeks.reduce((sum, w) => sum + (issue.weekly_logged?.[w.start_date] ?? 0), 0);
                  return (
                    <tr key={issue.key} className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      {/* Issue key */}
                      <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 px-4 py-3 min-w-[120px]">
                        <div className="flex flex-col gap-0.5">
                          <span className={`text-xs font-semibold ${getIssueTypeColor(issue.issue_type)}`}>
                            {issue.key}
                          </span>
                          {issue.epic_key && (
                            <span className="text-xs text-purple-500 dark:text-purple-400 truncate">
                              {issue.epic_key}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Issue Summary */}
                      <td className="sticky left-[120px] z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 px-3 py-3 min-w-[250px]">
                        <span className="text-sm text-slate-700 dark:text-slate-200 line-clamp-2" title={issue.summary}>
                          {issue.summary}
                        </span>
                        {issue.epic_key && (
                          <span className="text-xs text-purple-500 dark:text-purple-400 block truncate">
                            {issue.epic_summary}
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="sticky left-[370px] z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 px-3 py-3 min-w-[80px]">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(issue.status)}`}>
                          {issue.status}
                        </span>
                      </td>

                      {/* Assignee */}
                      <td className="sticky left-[450px] z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 px-3 py-3 text-sm text-slate-600 dark:text-slate-300 min-w-[160px] w-[160px] truncate">
                        {issue.assignee || '—'}
                      </td>

                      {/* Total logged */}
                      <td className="sticky left-[610px] z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 px-3 py-3 text-sm text-slate-600 dark:text-slate-300 min-w-[80px] tabular-nums text-right font-medium">
                        {totalLogged > 0 ? `${totalLogged.toFixed(1)}h` : '—'}
                      </td>

                      {/* Estimate */}
                      <td className="sticky left-[690px] z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 px-3 py-3 text-sm text-slate-600 dark:text-slate-300 min-w-[80px] tabular-nums shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]">
                        {formatHours(issue.original_estimate_seconds)}
                      </td>

                      {/* Weekly cells */}
                      {weeks.map(w => {
                        const status = getWeekStatus(w.start_date);
                        const cellKey = `${issue.key}-${w.start_date}`;
                        const loggedHours = issue.weekly_logged?.[w.start_date] ?? 0;
                        const plannedHours = pendingEdits[cellKey] !== undefined ? pendingEdits[cellKey] : (issue.weekly_planned?.[w.start_date] ?? 0);
                        const isSaving = savingCell === cellKey;

                        return (
                          <td
                            key={w.start_date}
                            className={`px-2 py-2 text-center min-w-[90px] ${status === 'current' ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                          >
                            {status === 'past' || status === 'current' ? (
                              <span className={`text-sm tabular-nums ${loggedHours > 0 ? 'text-slate-700 dark:text-slate-200 font-medium' : 'text-slate-300 dark:text-slate-600'}`}>
                                {loggedHours > 0 ? `${loggedHours.toFixed(1)}h` : '—'}
                              </span>
                            ) : (
                              <div className="relative">
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={pendingEdits[cellKey] !== undefined ? pendingEdits[cellKey] : plannedHours}
                                  onChange={e => handleCellChange(issue.key, w.start_date, e.target.value)}
                                  onBlur={() => handleCellBlur(issue.key, w.start_date)}
                                  disabled={isSaving}
                                  placeholder="0"
                                  className={`w-full rounded border text-center text-sm tabular-nums px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${plannedHours > 0
                                    ? 'border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                                    } disabled:opacity-50`}
                                />
                                {isSaving && (
                                  <ArrowPathIcon className="absolute right-1 top-1/2 -translate-y-1/2 size-3 animate-spin text-indigo-500" />
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}